/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import {
    Address,
    AddressPurpose,
    CountryRegionInfo,
    StateProvinceInfo
} from '@msdyn365-commerce/retail-proxy/dist/Entities/CommerceTypes.g';
import { set } from 'mobx';

import {
    AddressItemType,
    AddressValidationRuleType,
    IAddressDropdownsData,
    IAddressItem,
    IAddressValidationRule
} from './address-format.data';
import { AddressMetaData } from './address-meta-data';
import { IValidationRulesData } from '../../../modules/ext-new-checkout-billing-address/ext-new-checkout-billing-address.props.autogenerated';
import { ISuggestion } from './address-input';

/**
 *
 * Retail Service Address format parser.
 */
export class AddressFormat {
    private readonly countryRegionsInfo?: CountryRegionInfo[];

    private readonly addressPurposes?: AddressPurpose[];

    private readonly addressMetaData: AddressMetaData;

    constructor(countryRegionsInfo: CountryRegionInfo[], addressMetaData: AddressMetaData, addressPurposes: AddressPurpose[]) {
        this.countryRegionsInfo = countryRegionsInfo;
        this.addressMetaData = addressMetaData;
        this.addressPurposes = addressPurposes;
    }

    public getAddressFormat(countryRegionId: string, phoneOptional?: boolean, validationRules?: IValidationRulesData[]): IAddressItem[] {
        const countryRegionInfo = this._getCountryRegionInfo(countryRegionId);
        if (countryRegionInfo) {
            return this._getAddressDisplayFormat(countryRegionInfo, phoneOptional, validationRules);
        }
        return [];
    }

    public getCountryFormat(): { key?: string; value?: string }[] {
        return (this.countryRegionsInfo || []).map(countryRegion => {
            return {
                key: countryRegion.CountryRegionId,
                value: countryRegion.ShortName
            };
        });
    }

    public getStateFormat(stateProvinceInfo?: StateProvinceInfo[]): { key?: string; value?: string }[] {
        return (stateProvinceInfo || []).map(state => {
            return {
                key: state.StateId,
                value: state.StateName
            };
        });
    }

    public getAddressTypeFormat(): { key?: number; value?: string }[] {
        return (this.addressPurposes || []).map(addressPurpose => {
            return {
                key: addressPurpose.AddressType,
                value: addressPurpose.Description
            };
        });
    }

    public getPrefilledAddressDropdownData = (
        stateDefaultSelectionText: string,
        stateProvinceInfo?: StateProvinceInfo[]
    ): IAddressDropdownsData => {
        const defaultStateText = {
            key: '',
            value: stateDefaultSelectionText
        };
        const dropdownData: IAddressDropdownsData = {};

        dropdownData[AddressItemType[AddressItemType.ThreeLetterISORegionName]] = this.getCountryFormat();
        dropdownData[AddressItemType[AddressItemType.State]] = this.getStateFormat(stateProvinceInfo);
        dropdownData[AddressItemType[AddressItemType.State]].unshift(defaultStateText);
        dropdownData[AddressItemType[AddressItemType.AddressTypeValue]] = this.getAddressTypeFormat();

        return dropdownData;
    };

    public getTwoLetterISORegionName = (countryRegionId: string): string | undefined => {
        const countryRegionInfo = this._getCountryRegionInfo(countryRegionId);

        return countryRegionInfo!.ISOCode;
    };

    public validateAddressFormat = (
        address: Address,
        validationError: Address,
        countryRegionId: string,
        phoneOptional?: boolean,
        validationRules?: IValidationRulesData[],
        propertyName?: string
    ): boolean => {
        let isValid: boolean = true;
        let validationtor;
        const addressFormat = this.getAddressFormat(address.ThreeLetterISORegionName || countryRegionId, phoneOptional, validationRules);

        addressFormat.forEach(addressFormatItem => {
            if (!propertyName || (propertyName && addressFormatItem.name === propertyName)) {
                validationtor = this._inputValidation(addressFormatItem, validationError, address);
                if (validationtor !== undefined) {
                    isValid = validationtor;
                }
            }
        });

        return isValid;
    };

    public getTranformedAddress = (result: Microsoft.Maps.ISuggestionResult, stateProvinceInfo?: StateProvinceInfo[]): Address => {
        const address: Address = {};

        // Zip Code
        if (result.address.postalCode !== undefined) {
            address.ZipCode = result.address.postalCode;
        } else {
            address.ZipCode = '';
        }

        // State
        if (stateProvinceInfo) {
            const selectedState = stateProvinceInfo.find(state => state.StateName === result.address.adminDistrict);
            if (!selectedState) {
                address.State = '';
                address.StateName = result.address.adminDistrict;
            } else {
                address.State = selectedState.StateId;
                address.StateName = selectedState.StateName;
            }
        }

        // Street
        if (result.address.addressLine !== undefined) {
            address.Street = result.address.addressLine;
        } else {
            address.Street = ' ';
        }

        // City
        if (result.address.locality !== undefined) {
            address.City = result.address.locality;
        } else {
            address.City = '';
        }

        // District
        if (result.address.district !== undefined) {
            address.DistrictName = result.address.district;
            address.CountyName = result.address.district;
        } else {
            address.DistrictName = '';
        }

        // Formatted Address
        address.FullAddress = result.address.formattedAddress;

        return address;
    };

    public getTransformedAzureMapsAddress = (result: ISuggestion, stateProvinceInfo?: StateProvinceInfo[]): Address => {
        const address: Address = {};
        address.ZipCode = result.address.extendedPostalCode || result.address.postalCode || '';
        if (result.address.streetNumber && result.address.streetName) {
            address.Street = `${result.address.streetName} ${result.address.streetNumber}`;
        } else {
            address.Street = result.address.streetName || '';
        }

        address.City = result.address.municipality || '';
        address.DistrictName = result.address.countrySecondarySubdivision || '';
        address.CountyName = result.address.countrySecondarySubdivision || '';

        address.State = result.address.countrySubdivisionCode || '';
        address.StateName = result.address.countrySubdivisionName || '';

        address.FullAddress = result.address.freeformAddress || '';

        return address;
    };

    /**
     * Handles the selection of an Azure Maps suggestion and updates the address fields
     * @param suggestion - The selected suggestion from Azure Maps
     * @param stateProvinceInfo - Optional state/province information
     * @param addressSetter - Function to set address field values
     * @returns The transformed address
     */
    public handleAzureMapsSuggestionSelected(
        suggestion: ISuggestion,
        stateProvinceInfo?: StateProvinceInfo[],
        addressSetter?: (fieldName: string, value: string) => void
    ): Address {
        const address = this.getTransformedAzureMapsAddress(suggestion, stateProvinceInfo);
        // If no setter function is provided, just return the transformed address
        if (!addressSetter) {
            return address;
        }

        addressSetter('Street', address.Street || '');
        addressSetter('ZipCode', address.ZipCode || '');
        addressSetter('CountyName', address.CountyName || '');
        addressSetter('City', address.City || '');
        //BS CUSTOM - mapping the state gives errors in DK - might need to reevaluate later
        //addressSetter('State', address.State || address.StateName || '');
        addressSetter('DistrictName', address.DistrictName || '');
        addressSetter('FullAddress', address.FullAddress || '');

        return address;
    }

    private _inputValidation(addressFormatItem: IAddressItem, validationError: Address, address: Address): boolean | undefined {
        set(validationError, { [addressFormatItem.name]: null });
        for (const validationRule of addressFormatItem.validationRules || []) {
            if (!this._validateRegEx(address, addressFormatItem.name, validationRule)) {
                set(validationError, { [addressFormatItem.name]: validationRule.message });
                return false;
            }
        }
        return undefined;
    }

    private readonly _validateRegEx = (address: Address, propertyName: string, validationRule: IAddressValidationRule): boolean => {
        if (validationRule.regEx && validationRule.regEx.length > 0) {
            const regex = new RegExp(validationRule.regEx);
            const stringToValidate = address[propertyName as keyof Address]?.toString();
            return regex.test(stringToValidate || '');
        }
        return true;
    };

    private _getCountryRegionInfo(countryRegionId: string): CountryRegionInfo | undefined {
        return (this.countryRegionsInfo || []).find(countryRegion => {
            return (countryRegion.CountryRegionId || '').toLowerCase() === countryRegionId.toLowerCase();
        });
    }

    private _getAddressDisplayFormat(
        countryRegionInfo: CountryRegionInfo,
        phoneOptional?: boolean,
        validationRules?: IValidationRulesData[]
    ): IAddressItem[] {
        const addressDisplayItem: IAddressItem[] = [];
        if (countryRegionInfo && countryRegionInfo.AddressFormatLines) {
            const AddressTypeItem = this._extendAddressDisplayFormat(AddressItemType.AddressTypeValue, true);
            if (AddressTypeItem) {
                addressDisplayItem.push(AddressTypeItem);
            }

            const nameDisplayItem = this._extendAddressDisplayFormat(AddressItemType.Name, true);
            if (nameDisplayItem) {
                addressDisplayItem.push(nameDisplayItem);
            }

            countryRegionInfo.AddressFormatLines.forEach(formatLine => {
                if (formatLine.AddressComponentNameValue) {
                    const addressItem = this.addressMetaData.getItemFormat(formatLine.AddressComponentNameValue);
                    if (addressItem) {
                        addressItem.isNewLine = formatLine.NewLine || false;
                        addressDisplayItem.push(addressItem);
                    }
                }
            });

            const phoneDisplayItem = this._extendAddressDisplayFormat(AddressItemType.Phone, false);
            if (phoneDisplayItem) {
                //BS CUSTOM
                if (phoneOptional) {
                    //Remove the asterisk from the label, indicating that it is now optional
                    phoneDisplayItem.label = phoneDisplayItem.label.replace('*', '');

                    // Ignore the "required" validation rule
                    phoneDisplayItem.validationRules = phoneDisplayItem.validationRules?.filter(item => {
                        return item.type !== 'Required';
                    });
                }

                addressDisplayItem.push(phoneDisplayItem);
            }

            //BS CUSTOM
            if (validationRules && validationRules.length > 0) {
                validationRules.forEach(item => {
                    if (item.isHardValidation) {
                        var field;
                        switch (item.field) {
                            case 'Name':
                                field = AddressItemType.Name;
                                break;
                            case 'Phone':
                                field = AddressItemType.Phone;
                                break;
                            case 'ZipCode':
                                field = AddressItemType.ZipCode;
                                break;
                            case 'City':
                                field = AddressItemType.City;
                                break;
                            case 'Street':
                                field = AddressItemType.Street;
                                break;
                            case 'Country':
                                field = AddressItemType.ThreeLetterISORegionName;
                                break;
                            default:
                                field = AddressItemType.Name;
                                break;
                        }

                        const displayItem = this._extendAddressDisplayFormat(field, false);
                        if (displayItem) {
                            displayItem.validationRules?.push({
                                type: AddressValidationRuleType.Format,
                                regEx: item.regex ?? '',
                                message: item.error ?? ''
                            });
                        }
                    }
                });
            }

            const isPrimaryDisplayItem = this._extendAddressDisplayFormat(AddressItemType.IsPrimary, false);
            if (isPrimaryDisplayItem) {
                addressDisplayItem.push(isPrimaryDisplayItem);
            }
        }

        return addressDisplayItem;
    }

    private _extendAddressDisplayFormat(type: AddressItemType, isNewLine: boolean): IAddressItem | undefined {
        const addressItem = this.addressMetaData.getItemFormat(type);
        if (addressItem) {
            addressItem.isNewLine = addressItem.isNewLine ?? isNewLine;
        }
        return addressItem;
    }
}
